import { useNavigate } from 'react-router-dom';

import { ActionStickerCard, FeaturedIcon } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { OnboardingFlow } from './onboarding.types';
import { StickerCardId, useToolCardById } from '../Home/hooks/stickerCardMapping';

const RECOMMENDATIONS_TEXT = appStrings.modules.onboarding;

type RecommendationsProps = Readonly<{
  onboardingType: OnboardingFlow;
  recommendations: StickerCardId[];
}>;

export const Recommendations = ({ onboardingType, recommendations }: RecommendationsProps) => {
  const navigate = useNavigate();

  const { getStickerCard } = useToolCardById({ navigate });

  const mappedRecommendations = recommendations.map((recommendation) =>
    getStickerCard(recommendation),
  );

  const recommendationsWithDuplicatedRemoved = mappedRecommendations.filter(
    (recommendation, index, self) =>
      index === self.findIndex((t) => t.title === recommendation.title),
  );

  return (
    <>
      <FeaturedIcon iconName="FaceContent" size="xl" color="Colors/Foreground/fg-secondary" />
      <Box sx={recommendationsTextContainerStyle}>
        <Typography variant="display-sm-semibold" sx={recommendationsTextStyle} component="div">
          {RECOMMENDATIONS_TEXT.recommendations[onboardingType].title}
        </Typography>

        {recommendationsWithDuplicatedRemoved.length > 0 && (
          <>
            <Typography variant="text-md-regular">
              {RECOMMENDATIONS_TEXT.recommendations[onboardingType].description}
            </Typography>
            <Box sx={recommendationsContainerStyle}>
              {recommendationsWithDuplicatedRemoved.map((recommendation) => (
                <ActionStickerCard key={recommendation.title} {...recommendation} />
              ))}
            </Box>
          </>
        )}

        <Button hierarchy="Primary" size="lg" fullWidth onClick={() => navigate(APP_ROUTE.HOME)}>
          {RECOMMENDATIONS_TEXT.exploreDashboard}
        </Button>
      </Box>
    </>
  );
};

const recommendationsTextContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-lg'],
  textAlign: 'center',
  maxWidth: { xs: '100%', sm: '360px' },
  whiteSpace: 'pre-line',
});

const recommendationsTextStyle: SxProps<Theme> = ({ typography, breakpoints, backrs }) => ({
  [breakpoints.down('sm')]: {
    ...typography['display-xs-semibold'],
  },
  marginBottom: backrs.spacing['spacing-lg'],
});

const recommendationsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  maxHeight: `${114 * 3 + 24 * 2 + 8}px`, // height of 3 cards + 24px spacing * 2 + 8px padding
  overflowY: 'auto',
  textAlign: 'left',
  mt: backrs.spacing['spacing-4xl'],
  mb: backrs.spacing['spacing-2xl'],
  py: backrs.spacing['spacing-xs'],
  px: backrs.spacing['spacing-md'],
  boxSizing: 'border-box',
});
